/**
 * React Query hook for Case Outcomes (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { getOutcomeList } from 'services/CaseServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches case outcomes data
 *
 * @param {Object} params - Query parameters
 * @param {boolean} params.is_lead - Whether to fetch lead outcomes (default: false)
 * @param {string} params.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {number} params.limit - Limit for fetching outcomes (default: 10)
 * @param {string} params.token - Pagination token (default: '')
 * @param {string} params.direction - Pagination direction (default: 'forward')
 * @param {string} params.practice_area_id - Practice area filter (default: '')
 * @returns {Promise<Array>} - Array of case outcomes formatted as {label, value}
 */
const fetchCaseOutcomes = async ({
  is_lead = false,
  sort_order = 'alphabet_ascending',
  limit = 10,
  token = '',
  direction = 'forward',
  practice_area_id = '',
}) => {
  try {
    const response = await getOutcomeList(is_lead, sort_order, limit, token, direction, practice_area_id);

    // Transform data to match the format expected by components
    const outcomeData =
      response?.data?.['outcome-reason']?.map((val) => ({
        label: val.outcome_reason,
        value: val.outcome_reason,
      })) || [];

    return outcomeData;
  } catch (error) {
    console.error('Error fetching case outcomes:', error);
    throw error;
  }
};

/**
 * Hook to fetch case outcomes with React Query
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLead - Whether to fetch lead outcomes (default: false)
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {number} options.limit - Limit for fetching outcomes (default: 10)
 * @param {string} options.token - Pagination token (default: '')
 * @param {string} options.direction - Pagination direction (default: 'forward')
 * @param {string} options.practice_area_id - Practice area filter (default: '')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - caseOutcomeList, isCaseOutComeLoading, error, refetch, isFetching
 */
export const useCaseOutcomesQuery = ({
  isLead = false,
  sort_order = 'alphabet_ascending',
  limit = 10,
  token = '',
  direction = 'forward',
  practice_area_id = '',
  enabled = true,
} = {}) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.caseOutcomes.list(isLead, sort_order, limit, token, direction, practice_area_id),
    queryFn: () =>
      fetchCaseOutcomes({
        is_lead: isLead,
        sort_order,
        limit,
        token,
        direction,
        practice_area_id,
      }),
    enabled,
    staleTime: 15 * 60 * 1000, // Consider data stale after 15 minutes (outcomes change less frequently)
    gcTime: 20 * 60 * 1000, // Keep in cache for 20 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component mounts - use cache
    retry: 2,
  });

  return {
    caseOutcomeList: data || [],
    isCaseOutComeLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
